import { Routes } from '@angular/router';
import { authGuard } from './shared/guards/auth.guard';
import { adminGuard } from './shared/guards/admin.guard';
import { vereinGuard } from './guards/verein.guard';
import { licenseGuard } from './guards/license.guard';
import { adminOnlyGuard } from './shared/guards/admin-only.guard';

export const routes: Routes = [
  {
    path: '',
    redirectTo: '/dashboard',
    pathMatch: 'full'
  },
  {
    path: 'welcome',
    loadComponent: () => import('./components/landing/landing.component').then(m => m.LandingComponent),
    title: 'Willkommen | einfachnurlernen.de',
    data: { description: 'Starte in die einfachnurlernen.de und erhalte einen schnellen Überblick über die Funktionen.',
      canonical: 'https://www.einfachnurlernen.de/welcome' }
  },
  {
    path: 'login',
    loadComponent: () => import('./components/auth/login/login.component').then(m => m.LoginComponent),
    title: 'Login | einfachnurlernen.de',
    data: { description: 'Melde dich bei der einfachnurlernen.de an, um deine Kurse und Fortschritte zu laden.' }
  },
  {
    path: 'register',
    loadComponent: () => import('./components/auth/register/register.component').then(m => m.RegisterComponent),
    title: 'Registrieren | einfachnurlernen.de',
    data: { description: 'Erstelle einen Account, um Lernstände, Kurse und Prüfungen zu sichern.' }
  },
  {
    path: 'verify-email',
    loadComponent: () => import('./components/auth/verify-email/verify-email.component').then(m => m.VerifyEmailComponent),
    title: 'E-Mail verifizieren | einfachnurlernen.de',
    data: { description: 'Bestaetige deine E-Mail-Adresse, um alle Funktionen der Plattform freizuschalten.' }
  },
  {
    path: 'dashboard',
    loadComponent: () => import('./components/dashboard/dashboard.component').then(m => m.DashboardComponent),
    canActivate: [licenseGuard],
    title: 'Dashboard | einfachnurlernen.de',
    data: { description: 'Übersicht über Fortschritte, offene Aufgaben und nächste Schritte in deinen Kursen.' }
  },
  {
    path: 'calendar',
    loadComponent: () => import('./components/calendar/calendar.component').then(m => m.CalendarComponent),
    canActivate: [licenseGuard],
    title: 'Kalender | einfachnurlernen.de',
    data: { description: 'Kalenderansicht fuer deine Kurs-Termine.' }
  },
  {
    path: 'learning',
    title: 'Lernmodus | einfachnurlernen.de',
    data: { description: 'Trainiere Prüfungsfragen im Lernmodus mit Kategorien und direkten Auswertungen.' },
    canActivate: [licenseGuard],
    children: [
      {
        path: 'license',
        loadComponent: () => import('./components/learning/license-selection/license-selection.component').then(m => m.LicenseSelectionComponent),
        title: 'Lernmodus: Lizenz wählen | einfachnurlernen.de',
        data: { description: 'Wähle deine Bootsführerschein-Lizenz für den Lernmodus aus.' }
      },
      {
        path: 'categories',
        loadComponent: () => import('./components/learning/category-selection/category-selection.component').then(m => m.CategorySelectionComponent),
        title: 'Lernmodus: Kategorien | einfachnurlernen.de',
        data: { description: 'Wähle Themenkategorien, die du im Lernmodus üben möchtest.' }
      },
      {
        path: 'category-selection',
        redirectTo: 'license',
        pathMatch: 'full'
      },
      {
        path: 'question-view',
        loadComponent: () => import('./components/learning/question-view/question-view.component').then(m => m.QuestionViewComponent),
        title: 'Fragentraining | einfachnurlernen.de',
        data: { description: 'Beantworte Fragen im Lernmodus mit direkten Hinweisen und Erklärungen.' }
      },
      {
        path: 'questions/:schein/:kategorie',
        loadComponent: () => import('./components/learning/question-view/question-view.component').then(m => m.QuestionViewComponent),
        title: 'Fragentraining | einfachnurlernen.de',
        data: { description: 'Beantworte Fragen im Lernmodus mit direkten Hinweisen und Erklärungen.' }
      },
      {
        path: 'result',
        loadComponent: () => import('./components/learning/learning-result/learning-result.component').then(m => m.LearningResultComponent),
        title: 'Lernergebnis | einfachnurlernen.de',
        data: { description: 'Sieh dir deine Ergebnisse aus dem Lernmodus an und erkenne Verbesserungspotenzial.' }
      }
    ]
  },
  {
    path: 'lernstoff',
    canActivate: [licenseGuard],
    children: [
      {
        path: '',
        loadComponent: () => import('./components/learning-material/learning-material.component').then(m => m.LearningMaterialComponent),
        title: 'Lernstoff | einfachnurlernen.de',
        data: { description: 'Alle Lernmaterialien zum Bootsführerschein gebündelt an einem Ort.' }
      },
      {
        path: ':license',
        loadComponent: () => import('./components/learning-material/questions-table/questions-table.component').then(m => m.QuestionsTableComponent),
        title: 'Lernstoff Fragen | einfachnurlernen.de',
        data: { description: 'Vollständige Fragenliste für die gewählte Lizenz mit Antworten und Erklärungen.' }
      }
    ]
  },
  {
    path: 'materialien',
    loadComponent: () => import('./components/learning/materialien/materialien.component').then(m => m.MaterialienComponent),
    canActivate: [licenseGuard],
    title: 'Materialien | einfachnurlernen.de',
    data: { description: 'Zusätzliche Materialien, Downloads und Hilfen für die Prüfungsvorbereitung.' }
  },
  {
    path: 'exam',
    title: 'Prüfungsmodus | einfachnurlernen.de',
    data: { description: 'Starte den Prüfungsmodus mit offiziellen Prüfungsbögen und Zeitlimit.' },
    canActivate: [licenseGuard],
    children: [
      {
        path: '',
        redirectTo: 'license',
        pathMatch: 'full'
      },
      {
        path: 'license',
        loadComponent: () => import('./components/exam/exam-license-selection/exam-license-selection.component').then(m => m.ExamLicenseSelectionComponent),
        title: 'Prüfungsmodus: Lizenz wählen | einfachnurlernen.de',
        data: { description: 'Wähle deine Lizenz, um eine Prüfung mit passenden Bögen zu starten.' }
      },
      {
        path: 'boegen',
        loadComponent: () => import('./components/exam/exam-bogen-selection/exam-bogen-selection.component').then(m => m.ExamBogenSelectionComponent),
        title: 'Prüfungsbögen | einfachnurlernen.de',
        data: { description: 'Wähle den Prüfungsbogen, den du unter Echtbedingungen bearbeiten möchtest.' }
      },
      {
        path: 'result',
        loadComponent: () => import('./components/exam/exam-result/exam-result.component').then(m => m.ExamResultComponent),
        title: 'Prüfungsergebnis | einfachnurlernen.de',
        data: { description: 'Auswertung deiner letzten Prüfung inklusive richtiger Lösungen.' }
      },
      {
        path: ':id',
        loadComponent: () => import('./components/exam/exam-mode/exam-mode.component').then(m => m.ExamModeComponent),
        title: 'Prüfung starten | einfachnurlernen.de',
        data: { description: 'Bearbeite einen Prüfungsbogen mit Zeitlimit und Live-Auswertung.' }
      }
    ]
  },
  {
    path: 'statistics',
    loadComponent: () => import('./components/statistics/statistics.component').then(m => m.StatisticsComponent),
    canActivate: [authGuard, licenseGuard],
    title: 'Statistiken | einfachnurlernen.de',
    data: { description: 'Analysen zu deinen Lernergebnissen, Erfolgsquoten und offenen Aufgaben.' }
  },
  {
    path: 'statistics/learning-time',
    loadComponent: () => import('./components/statistics/learning-time/learning-time.component').then(m => m.LearningTimeComponent),
    canActivate: [authGuard, licenseGuard],
    title: 'Lernzeit | einfachnurlernen.de',
    data: { description: 'Auswertung deiner Lernzeiten je Tag und Kurs.' }
  },
  {
    path: 'profile',
    loadComponent: () => import('./components/profile/profile.component').then(m => m.ProfileComponent),
    canActivate: [authGuard],
    title: 'Profil | einfachnurlernen.de',
    data: { description: 'Verwalte deine Profildaten, Einstellungen und Benachrichtigungen.' }
  },
  {
    path: 'license-request',
    loadComponent: () => import('./components/license-request/license-request.component').then(m => m.LicenseRequestComponent),
    title: 'Lizenz beantragen | einfachnurlernen.de',
    data: { description: 'Beantrage oder aktiviere deine Lernlizenz, um alle Inhalte freizuschalten.',
      canonical: 'https://www.einfachnurlernen.de/license-request' }
  },
  {
    path: 'admin',
    loadComponent: () => import('./components/admin/admin.component').then(m => m.AdminComponent),
    canActivate: [adminGuard, licenseGuard],
    title: 'Admin | einfachnurlernen.de',
    data: { description: 'Admin-Bereich fuer Nutzer, Vereine, Kurse und Inhalte.' },
    children: [
      {
        path: '',
        redirectTo: 'users',
        pathMatch: 'full'
      },
      {
        path: 'users',
        loadComponent: () => import('./components/admin/users/users.component').then(m => m.AdminUsersComponent),
        title: 'Admin: Nutzer | einfachnurlernen.de',
        data: { description: 'Verwalte Nutzerkonten, Rollen und Berechtigungen.' }
      },
      {
        path: 'vereine',
        loadComponent: () => import('./components/admin/vereine/vereine.component').then(m => m.AdminVereineComponent),
        title: 'Admin: Vereine | einfachnurlernen.de',
        data: { description: 'Verwalte Vereinsdaten, Kontingente und Verantwortliche.' }
      },
      {
        path: 'kurse',
        loadComponent: () => import('./components/admin/courses/courses.component').then(m => m.AdminCoursesComponent),
        title: 'Admin: Kurse | einfachnurlernen.de',
        data: { description: 'Pflege Kursinformationen, Termine und Teilnehmer.' }
      },
      {
        path: 'exam-decks',
        loadComponent: () => import('./components/admin/exam-decks/exam-decks.component').then(m => m.ExamDecksComponent),
        title: 'Admin: Prüfungsbögen | einfachnurlernen.de',
        data: { description: 'Verwalte Pruefungsboegen und Decks fuer die Pruefungsmodi.' }
      },
      {
        path: 'questions',
        loadComponent: () => import('./components/admin/questions/questions.component').then(m => m.QuestionsComponent),
        title: 'Admin: Fragen | einfachnurlernen.de',
        data: { description: 'Pflege Fragenkataloge, Antworten und Zuordnungen.' }
      },
      {
        path: 'materialien',
        loadComponent: () => import('./components/admin/materialien/materialien.component').then(m => m.AdminMaterialienComponent),
        title: 'Admin: Materialien | einfachnurlernen.de',
        data: { description: 'Verwalte Medien, Downloads und zusaetzliche Lernmaterialien.' }
      },
      {
        path: 'promo-codes',
        loadComponent: () => import('./components/admin/promo-codes/promo-codes.component').then(m => m.PromoCodesComponent),
        title: 'Admin: Promo-Codes | einfachnurlernen.de',
        data: { description: 'Lege Promo-Codes an und verwalte deren Nutzung.' }
      },
      {
        path: 'guide-articles',
        loadComponent: () => import('./components/admin/guide-articles/guide-articles.component').then(m => m.AdminGuideArticlesComponent),
        canActivate: [adminOnlyGuard],
        title: 'Admin: Guide-Artikel | einfachnurlernen.de',
        data: { description: 'Guide-Artikel mit HTML-Inhalten und Screenshots pflegen.' }
      },
      {
        path: 'mail-templates',
        loadComponent: () => import('./components/admin/mail-templates/mail-templates.component').then(m => m.MailTemplatesComponent),
        canActivate: [adminOnlyGuard],
        title: 'Admin: E-Mail-Vorlagen | einfachnurlernen.de',
        data: { description: 'Verwalte System-E-Mail-Vorlagen für Registrierung und Verlängerungen.' }
      }
    ]
  },
  {
    path: 'verein-dashboard',
    loadComponent: () => import('./components/vereine/verein-admin-dashboard/verein-admin-dashboard.component').then(m => m.VereinAdminDashboardComponent),
    canActivate: [vereinGuard, licenseGuard],
    title: 'Verein-Dashboard | einfachnurlernen.de',
    data: { description: 'Verwalte Vereinsmitglieder, Kurse und Materialien.' },
    children: [
      {
        path: '',
        redirectTo: 'mitglieder',
        pathMatch: 'full'
      },
      {
        path: 'mitglieder',
        loadComponent: () => import('./components/vereine/verein-mitglieder/verein-mitglieder.component').then(m => m.VereinMitgliederComponent),
        canActivate: [vereinGuard],
        title: 'Verein: Mitglieder | einfachnurlernen.de',
        data: { description: 'Uebersicht und Verwaltung der Vereinsmitglieder.' }
      },
      {
        path: 'kurse',
        loadComponent: () => import('./components/vereine/verein-kurse/verein-kurse.component').then(m => m.VereinKurseComponent),
        canActivate: [vereinGuard],
        title: 'Verein: Kurse | einfachnurlernen.de',
        data: { description: 'Planung und Verwaltung der Vereinskurse.' }
      },
      {
        path: 'materialien',
        loadComponent: () => import('./components/vereine/verein-materialien/verein-materialien.component').then(m => m.VereinMaterialienComponent),
        canActivate: [vereinGuard],
        title: 'Verein: Materialien | einfachnurlernen.de',
        data: { description: 'Materialien und Dokumente fuer Vereinsmitglieder bereitstellen.' }
      },
      {
        path: 'pruefungsboegen',
        loadComponent: () => import('./components/vereine/verein-exam-decks/verein-exam-decks.component').then(m => m.VereinExamDecksComponent),
        canActivate: [vereinGuard],
        title: 'Verein: Pruefungsboegen | einfachnurlernen.de',
        data: { description: 'Verwalte Pruefungsboegen fuer Vereinskurse.' }
      }
    ]
  },
  {
    path: 'vereine/:id/kurse',
    redirectTo: '/verein-dashboard/kurse',
    pathMatch: 'full'
  },
  {
    path: 'vereine/:id/mitglieder',
    redirectTo: '/verein-dashboard/mitglieder',
    pathMatch: 'full'
  },
  {
    path: 'vereine/:id/materialien',
    redirectTo: '/verein-dashboard/materialien',
    pathMatch: 'full'
  },
  {
    path: 'guide',
    loadComponent: () => import('./components/guide/guide.component').then(m => m.GuideComponent),
    title: 'Anleitungen | einfachnurlernen.de',
    data: {
      description: 'Öffentlicher Anleitungsbereich ohne Login mit kompakten Schritt-für-Schritt-Guides.',
      canonical: 'https://www.einfachnurlernen.de/guide'
    }
  },
  {
    path: 'guide/:slug',
    loadComponent: () => import('./components/guide/guide.component').then(m => m.GuideComponent),
    title: 'Guide | einfachnurlernen.de',
    data: { description: 'Detaillierter Artikel aus den Guides.' }
  },
  {
    path: 'impressum',
    loadComponent: () => import('./components/legal/impressum/impressum.component').then(m => m.ImpressumComponent),
    title: 'Impressum | einfachnurlernen.de',
    data: { description: 'Rechtliche Angaben und Kontakt zur einfachnurlernen.de.',
      canonical: 'https://www.einfachnurlernen.de/impressum' }
  },
  {
    path: 'datenschutz',
    loadComponent: () => import('./components/legal/datenschutz/datenschutz.component').then(m => m.DatenschutzComponent),
    title: 'Datenschutz | einfachnurlernen.de',
    data: { description: 'Informationen zum Datenschutz und zur Verarbeitung deiner Daten.',
      canonical: 'https://www.einfachnurlernen.de/datenschutz' }
  },
  {
    path: 'agb',
    loadComponent: () => import('./components/legal/agb/agb.component').then(m => m.AgbComponent),
    title: 'AGB | einfachnurlernen.de',
    data: { description: 'Allgemeine Geschäftsbedingungen für die Nutzung der einfachnurlernen.de.',
      canonical: 'https://www.einfachnurlernen.de/agb' }
  },
  {
    path: 'quellangaben',
    loadComponent: () => import('./components/legal/quellangaben/quellangaben.component').then(m => m.QuellangabenComponent),
    title: 'Quellangaben | einfachnurlernen.de',
    data: { description: 'Quellen- und Bildnachweise der einfachnurlernen.de.',
      canonical: 'https://www.einfachnurlernen.de/quellangaben' }
  },
  {
    path: 'ueber-mich',
    loadComponent: () => import('./components/legal/ueber-mich/ueber-mich.component').then(m => m.UeberMichComponent),
    title: 'Ueber mich | einfachnurlernen.de',
    data: { description: 'Hintergrundinformationen zur einfachnurlernen.de.',
      canonical: 'https://www.einfachnurlernen.de/ueber-mich' }
  },
  {
    path: 'disclaimer',
    loadComponent: () => import('./components/legal/disclaimer/disclaimer.component').then(m => m.DisclaimerComponent),
    title: 'Disclaimer | einfachnurlernen.de',
    data: { description: 'Haftungsausschluss und rechtliche Hinweise.',
      canonical: 'https://www.einfachnurlernen.de/disclaimer' }
  },
  {
    path: 'debug/image',
    loadComponent: () => import('./components/debug/image-test/image-test.component').then(m => m.ImageTestComponent),
    title: 'Bild-Test | einfachnurlernen.de',
    data: { description: 'Testseite um Fragebilder per ID direkt zu laden.' }
  },
  {
    path: '**',
    redirectTo: '/dashboard'
  }
];
